import React from 'react';
import { colors } from './TabNavigation';
import { isSentryEnabled } from './sentry';
import useIsMobile from './useIsMobile';

// Real support address on the cuspli.com domain (mailbox/forwarding managed in Vercel domain settings).
export const CONTACT_EMAIL = 'hello@cuspli.com';
const LAST_UPDATED = 'July 5, 2026';

const serif = "'Cormorant Garamond', Georgia, serif";

function Eyebrow({ children }) {
  const isMobile = useIsMobile();
  return (
    <p style={{
      fontSize: isMobile ? '12px' : '10px',
      fontWeight: '500',
      letterSpacing: '2.5px',
      textTransform: 'uppercase',
      color: colors.gold,
      marginBottom: '16px',
      textAlign: 'center',
    }}>
      {children}
    </p>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginTop: '36px' }}>
      {title && (
        <h2 style={{
          fontFamily: serif,
          fontSize: 'clamp(20px, 5vw, 26px)',
          fontWeight: '400',
          color: colors.textPrimary,
          margin: '0 0 12px',
          lineHeight: '1.25',
        }}>
          {title}
        </h2>
      )}
      {children}
    </div>
  );
}

function P({ children }) {
  const isMobile = useIsMobile();
  return (
    <p style={{
      fontSize: isMobile ? '17px' : '15px',
      fontWeight: '300',
      color: colors.textSecondary,
      lineHeight: '1.75',
      margin: '0 0 14px',
    }}>
      {children}
    </p>
  );
}

function MailLink() {
  return (
    <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: colors.gold, textDecoration: 'none', borderBottom: `1px solid ${colors.borderGold}` }}>
      {CONTACT_EMAIL}
    </a>
  );
}

function LegalLayout({ title, children, onNavigate }) {
  const isMobile = useIsMobile();
  return (
    <div style={{ maxWidth: '680px', margin: '0 auto', padding: '0 24px' }}>
      <Eyebrow>Cuspli</Eyebrow>
      <h1 style={{
        fontFamily: serif,
        fontSize: 'clamp(28px, 8vw, 40px)',
        fontWeight: '400',
        color: colors.textPrimary,
        textAlign: 'center',
        margin: '0 0 8px',
        lineHeight: '1.15',
      }}>
        {title}
      </h1>
      <p style={{
        fontSize: isMobile ? '14px' : '12px',
        fontStyle: 'italic',
        color: colors.textMuted,
        textAlign: 'center',
        margin: '0 0 8px',
      }}>
        Last updated {LAST_UPDATED}
      </p>

      <div style={{ marginTop: '32px' }}>
        {children}
      </div>

      <div style={{
        marginTop: '56px',
        paddingTop: '28px',
        borderTop: `1px solid ${colors.borderSubtle}`,
        textAlign: 'center',
      }}>
        <button
          onClick={() => { onNavigate('home'); window.scrollTo({ top: 0 }); }}
          style={{
            background: 'none',
            border: 'none',
            fontSize: isMobile ? '14px' : '12px',
            fontStyle: 'italic',
            color: colors.textMuted,
            cursor: 'pointer',
            fontFamily: 'inherit',
            textDecoration: 'underline',
          }}
        >
          ← Back to Cuspli
        </button>
      </div>
    </div>
  );
}

export function PrivacyPolicy({ onNavigate }) {
  return (
    <LegalLayout title="Privacy Policy" onNavigate={onNavigate}>
      <P>
        Cuspli is built to need as little of your information as possible. There are no
        accounts to create, no advertising trackers, and nothing about you is ever sold.
        This page explains, in plain terms, exactly what happens to the details you enter.
      </P>

      <Section title="What you give us">
        <P>
          To calculate a birth chart, Cuspli uses your birth date, birth time, and birth
          place. For the compatibility feature, you also enter a second person's birth
          details. You may optionally type a name or label to help you tell your saved
          charts apart.
        </P>
      </Section>

      <Section title="How it's used — and why we don't keep it">
        <P>
          When you calculate a chart, those details are sent to our server for the single
          purpose of doing the astronomical maths, and the result is sent straight back to
          your device. We do not store your birth details on our servers, and we do not
          keep them in any database. Once your chart is calculated, the request is done.
        </P>
      </Section>

      <Section title="What stays on your device">
        <P>
          Any chart you choose to save is stored only in your own web browser, on your own
          device (using your browser's local storage). It never reaches us. If you delete a
          saved chart in the app, or clear your browser's data, it's gone — there is no
          copy on our side to ask us to remove.
        </P>
      </Section>

      <Section title="The one outside service we use">
        <P>
          To turn a birth place you type (like “London”) into map coordinates and the right
          timezone, Cuspli sends that place name to Open-Meteo (open-meteo.com), a free
          geographic lookup service. Only the place name and its coordinates are sent for
          this lookup — never your name, and never the second person's details beyond their
          birth place.
        </P>
      </Section>

      <Section title="Cookies and analytics">
        <P>
          Cuspli uses Vercel Web Analytics to understand how many people visit and which pages
          get used. It is privacy-friendly by design: no cookies, no cross-site tracking, and no
          personal data — only anonymous, aggregate counts. Because nothing personal is stored,
          there is no cookie banner to click through. Cuspli uses no advertising trackers.
        </P>
      </Section>

      {isSentryEnabled && (
        <Section title="Error monitoring">
          <P>
            To find and fix problems quickly, Cuspli uses Sentry to capture technical error
            diagnostics when something goes wrong in the app — for example, an error message and
            where in the code it occurred. These reports are deliberately configured to exclude
            your birth details and other personal data. Sentry processes this technical data on
            our behalf; see sentry.io for their practices.
          </P>
        </Section>
      )}

      <Section title="Children">
        <P>
          Cuspli is intended for a general audience and is not directed at children under 16.
        </P>
      </Section>

      <Section title="Changes to this policy">
        <P>
          We may update this policy as Cuspli grows. When we do, we'll change the “last
          updated” date at the top of this page.
        </P>
      </Section>

      <Section title="Contact">
        <P>
          Questions about your privacy? Email us at <MailLink />.
        </P>
      </Section>
    </LegalLayout>
  );
}

export function Disclaimer({ onNavigate }) {
  return (
    <LegalLayout title="Disclaimer" onNavigate={onNavigate}>
      <P>
        Cuspli is made for entertainment, reflection, and curiosity. We hope it gives you
        an interesting lens on yourself and the moment you're in — nothing more, and nothing
        it asks you to take too seriously.
      </P>

      <Section title="Not professional advice">
        <P>
          Cuspli's readings are not a substitute for professional medical, psychological,
          legal, or financial advice. Please don't make important life decisions based only
          on an astrological interpretation. If you need real guidance, speak to a qualified
          professional.
        </P>
      </Section>

      <Section title="About astrology">
        <P>
          Astrology is not a science, and its interpretations are not scientifically proven.
          The astronomical positions Cuspli calculates are accurate; the meanings drawn from
          them are a traditional, symbolic art meant for insight and enjoyment.
        </P>
      </Section>

      <Section title="Contact">
        <P>
          Questions? Email us at <MailLink />.
        </P>
      </Section>
    </LegalLayout>
  );
}

export function TermsOfUse({ onNavigate }) {
  return (
    <LegalLayout title="Terms of Use" onNavigate={onNavigate}>
      <P>
        By using Cuspli, you agree to these simple terms. We've kept them short and plain.
      </P>

      <Section title="Provided as-is">
        <P>
          Cuspli is offered free, “as is,” without warranties of any kind. We do our best to
          make it accurate and available, but we can't guarantee it will always be correct,
          uninterrupted, or error-free.
        </P>
      </Section>

      <Section title="Use at your own risk">
        <P>
          You're welcome to use Cuspli for personal, non-commercial curiosity. To the extent
          allowed by law, we aren't liable for any decisions you make or any loss that results
          from using the app. See the <em>Disclaimer</em> for more.
        </P>
      </Section>

      <Section title="Content">
        <P>
          The Cuspli name, design, and written interpretations belong to us — please don't
          copy or redistribute them wholesale. The birth details and charts you create are
          yours; they stay on your device.
        </P>
      </Section>

      <Section title="Changes">
        <P>
          We may add, change, or remove features, and we may update these terms over time.
          The “last updated” date at the top reflects the current version.
        </P>
      </Section>

      <Section title="Contact">
        <P>
          Questions about these terms? Email us at <MailLink />.
        </P>
      </Section>
    </LegalLayout>
  );
}
